import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';

interface NavItem {
  label: string;
  path: string;
  permission?: string;
  isActivePath: (path: string) => boolean;
}

// Single source of truth for nav items + the permission needed to see them,
// replacing the old adminPages/employeePages string arrays (which had
// drifted out of sync — 'Business Tax' vs 'BusinessTax' meant the button
// silently did nothing for non-admins) and the module-scope localStorage
// read that made role/permission changes require a full page reload.
const NAV_ITEMS: NavItem[] = [
  { label: 'Employees', path: '/employees', permission: 'employees.view', isActivePath: (p) => p.startsWith('/employees') },
  { label: 'Clients', path: '/clients', permission: 'clients.view', isActivePath: (p) => p.startsWith('/clients') },
  { label: 'Sales', path: '/sales', permission: 'sales.view', isActivePath: (p) => p.startsWith('/sales') },
  { label: 'Business Tax', path: '/businessTax', permission: 'yearlyBusinessTax.view', isActivePath: (p) => p.startsWith('/businessTax') },
  { label: 'Payroll', path: '/payroll', permission: 'payroll.view', isActivePath: (p) => p.startsWith('/payroll') },
  { label: 'Reports', path: '/reports', permission: 'reports.view', isActivePath: (p) => p.startsWith('/reports') },
  { label: 'Doc Gen', path: '/document-generator', permission: 'templates.view', isActivePath: (p) => p.startsWith('/document-generator') || p.startsWith('/templates') }
];

const settings = ['Logout'];

interface NavbarProps {
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onLogout }) => {
  const theme = useTheme();
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pages = NAV_ITEMS.filter(
    (item) => !item.permission || hasPermission(item.permission)
  );

  const handleLogout = () => {
    onLogout();
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handlePageClick = (page: NavItem) => {
    handleCloseNavMenu();
    navigate(page.path);
  };

  // Helper to determine active state
  const isActive = (page: NavItem) => page.isActivePath(location.pathname);

  return (
    <AppBar position="sticky" color="default" sx={{ borderRadius: 0, boxShadow: theme.shadows[1] }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Box for Web View Starts Here */}
          <Box
            sx={{
              width: '100%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{
                  mr: 2,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 800,
                  letterSpacing: '.1rem',
                  color: theme.palette.primary.main,
                  textDecoration: 'none'
                }}
              >
                AK Fintax
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 2 }}>
              {pages.map((page) => (
                <Button
                  key={page.path}
                  onClick={() => handlePageClick(page)}
                  sx={{
                    my: 2,
                    color: isActive(page) ? theme.palette.primary.main : 'text.secondary',
                    fontWeight: isActive(page) ? 700 : 500,
                    display: 'block',
                    backgroundColor: isActive(page) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  {page.label}
                </Button>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body1"
                sx={{ display: { xs: 'none', lg: 'block' }, fontWeight: 600, color: 'text.secondary' }}
              >
                {user && user.firstName}
              </Typography>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    alt={user && user.firstName}
                    src="/static/images/avatar/2.jpg" // Placeholder or actual image path
                    sx={{ bgcolor: theme.palette.primary.main }}
                  >
                    {user && user.firstName ? user.firstName.charAt(0) : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem
                    key={setting}
                    onClick={setting === 'Logout' ? handleLogout : handleCloseUserMenu}
                  >
                    <Typography textAlign="center">{setting}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          {/* Box for Web View Ends Here */}

          {/* Box for Mobile View Starts Here */}
          <Box
            sx={{
              width: '100%',
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' }
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page.path} onClick={() => handlePageClick(page)}>
                  <Typography textAlign="center">{page.label}</Typography>
                </MenuItem>
              ))}
            </Menu>

            <Typography
              variant="h6"
              noWrap
              component="a"
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              AK Fintax
            </Typography>

            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    alt={user && user.firstName}
                    sx={{ bgcolor: theme.palette.primary.main }}
                  >
                    {user && user.firstName ? user.firstName.charAt(0) : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem
                    key={setting}
                    onClick={setting === 'Logout' ? handleLogout : handleCloseUserMenu}
                  >
                    <Typography textAlign="center">{setting}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          {/* Box for Mobile View Ends Here */}
        </Toolbar >
      </Container >
    </AppBar >
  );
};

export default Navbar;
